import { ref, watch, nextTick } from "vue";
import SignaturePad from "signature_pad";

/**
 * Wraps the `signature_pad` canvas library with the mount-timing handling this platform's usage
 * needs: the canvas element is frequently gated behind more than one condition at once (a v-if
 * on the caller's side, a lazy-rendering parent like Vuetify's v-expansion-panel, or both), and
 * the initial signature value is often fetched asynchronously and can arrive after mount. Watch
 * the canvas ref itself (not a single gating condition) so init retries whenever the element
 * actually appears, and reload a late-arriving value without ever clobbering an in-progress draw.
 *
 * Also captures the signature as stroke geometry, not just as a PNG. A raster signature carries its
 * line thickness in its pixels, so shrinking it into a small form field shrinks the strokes with it
 * until the fax's 1-bit conversion drops them and the payor receives a blank signature line.
 * Coordinates have no thickness, so the backend can re-draw them as vector strokes at a line width
 * fixed in page points (see backend portal/logic/process_pdf/signature_render.py).
 *
 * @param {object} options
 * @param {import('vue').Ref<string|null>} options.modelValue - current base64 signature image
 * @param {import('vue').Ref<boolean>} [options.disabled] - when true, drawing is disabled
 * @param {(value: string|null, strokes: object|null) => void} options.onChange - called on stroke
 *   end with the new base64 image and its stroke geometry (geometry is null when it can't be
 *   captured faithfully, or on clear)
 * @returns {{canvasRef: import('vue').Ref<HTMLCanvasElement|null>, clear: () => void}}
 */

// Bumped only on an incompatible shape change; the backend refuses versions it can't draw.
const STROKES_VERSION = 1;
// One decimal is ~0.1 CSS px of precision — finer than the pad can capture and far finer than the
// stroke width, so rounding here only trims payload size.
const COORDINATE_DECIMALS = 1;

/**
 * Convert signature_pad's toData() into the stored shape: coordinates only.
 *
 * Deliberately drops everything else the library records. penColor/dotSize/min/maxWidth don't apply
 * to a vector re-draw at a fixed width, and per-point `time`/`pressure` amount to a behavioural
 * record of how someone signs — not something to store when nothing renders it.
 */
function serializeStrokes(pad, canvas) {
  const pointGroups = pad.toData() || [];
  const strokes = pointGroups
    .map((group) => ({
      points: (group?.points || []).map((point) => [
        Number(point.x.toFixed(COORDINATE_DECIMALS)),
        Number(point.y.toFixed(COORDINATE_DECIMALS)),
      ]),
    }))
    .filter((stroke) => stroke.points.length > 0);
  if (!strokes.length) {
    return null;
  }
  // CSS-pixel dimensions, matching the coordinate space toData() reports: the 2d context is scaled
  // by devicePixelRatio, so the backing store is larger than these but the points are not.
  return {
    version: STROKES_VERSION,
    width: canvas.offsetWidth,
    height: canvas.offsetHeight,
    strokes,
  };
}

export function useSignaturePad({ modelValue, disabled, onChange }) {
  const canvasRef = ref(null);
  const signaturePad = ref(null);
  // True while the canvas shows ink that toData() can't account for — i.e. a stored signature
  // loaded with fromDataURL, which paints pixels without recording point groups. Drawing on top of
  // that would report geometry for the new strokes only, so the two representations would disagree
  // about what the signature is. Report no geometry instead and let the backend fall back to the
  // raster path; clearing the pad makes capture faithful again.
  const hasUnrecordedInk = ref(false);

  function init() {
    if (signaturePad.value || !canvasRef.value) {
      return;
    }
    const canvas = canvasRef.value;
    signaturePad.value = new SignaturePad(canvas, {
      backgroundColor: "rgb(255, 255, 255)",
      penColor: "rgb(0, 0, 0)",
    });
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext("2d").scale(ratio, ratio);

    if (modelValue.value) {
      signaturePad.value.fromDataURL(modelValue.value);
      hasUnrecordedInk.value = true;
    }
    if (disabled?.value) {
      signaturePad.value.off();
    }

    signaturePad.value.addEventListener("endStroke", () => {
      onChange(
        signaturePad.value.toDataURL(),
        hasUnrecordedInk.value ? null : serializeStrokes(signaturePad.value, canvas)
      );
    });
  }

  watch(
    canvasRef,
    async (canvas) => {
      if (!canvas) {
        signaturePad.value?.off();
        signaturePad.value?.clear();
        signaturePad.value = null;
        hasUnrecordedInk.value = false;
        return;
      }
      await nextTick();
      init();
    },
    { immediate: true }
  );

  watch(modelValue, (value) => {
    if (value && signaturePad.value && signaturePad.value.isEmpty()) {
      signaturePad.value.fromDataURL(value);
      hasUnrecordedInk.value = true;
    }
  });

  if (disabled) {
    watch(disabled, (isDisabled) => {
      if (!signaturePad.value) {
        return;
      }
      if (isDisabled) {
        signaturePad.value.off();
      } else {
        signaturePad.value.on();
      }
    });
  }

  function clear() {
    signaturePad.value?.clear();
    hasUnrecordedInk.value = false;
    onChange(null, null);
  }

  return { canvasRef, clear };
}
